// @flow
import './MarketingConsentPage.scss';

import {
  AccordionText,
  Button, ButtonGroup,
  CardWrapper,
  TitleWithUnderLine
} from '@boi/core/lib';
import { Form, FormikProps, withFormik } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import type { Location } from 'react-router-dom';
import { Link } from 'react-router-dom';
import * as yup from 'yup';

import { ExternalLink } from '../../components/ExternalLink/ExternalLink';
import { MobileBackButton } from '../../components/MobileBackButton';
import ProgressBar from '../../components/ProgressBar';
import { desktopBackButtonClass } from '../../constants/commonConstants';
import { dataPrivacyHomeUrl } from '../../constants/externalLinks';
import { HOME_INSURANCE_TYPE } from '../../constants/insuranceTypeConstants';
import {
  HOME_JOURNEY,
  JOURNEY_YOUR_QUOTE,
  MOTOR_TRAVEL_JOURNEY,
  STAGES_HOME_JOURNEY,
  STAGES_MOTOR_TRAVEL_JOURNEY
} from '../../constants/journey';
import { DIRECT_DEBIT_STORAGE, HOME_QUOTE_THANK_YOU_STORAGE } from '../../constants/sessionStorage';
import { HOME_LATEST_QUOTE_STORAGE, HOME_RESUME_QUOTE } from '../../constants/sessionStorage/homeStorageIdentifiers';
import { AGENT_CLICKED_REFERENCE_QUOTES } from '../../constants/sessionStorage/userStorageIdentifiers';
import {
  commonFormStylesIdentifier,
  commonPageStylesIdentifier
} from '../../constants/styleConstants';
import {
  getItemFromSessionStorage,
  getObjectFromSessionStorage,
  isAgent, isFieldError, removeFromSessionStorage,
  saveInSessionStorage
} from '../../helpers';
import { saveData, saveDataInLatestQuote } from '../../helpers/HomeCommonMethods';
import { routes } from '../../routes';
import type { DirectDebitType } from '../../types/commonTypes';
import { UserProfileContext } from '../../UserProfile';
import AnalyzeTransaction from './AnalyzeTransaction';
import ConsentText from './ConsentText';

const className = 'c-MarketingConsentPage';
interface Props {
  location: Location;
}

// eslint-disable-next-line complexity
const MarketingConsent = (props: FormikProps & Props) => {
  const { values, setFieldValue, setFieldTouched, match, errors, touched, history, location  } = props;
  const { userProfile } = useContext(UserProfileContext);
  const insuranceType = match.params.type;
  const FULL_DIRECT_DEBIT_STORAGE_IDENTIFIER = `${insuranceType}${DIRECT_DEBIT_STORAGE}`;
  const state =
    location?.state ||
    getObjectFromSessionStorage(FULL_DIRECT_DEBIT_STORAGE_IDENTIFIER);
  const [nextButton, setNextButton] = useState(false);

  // eslint-disable-next-line complexity
  const goToNextPage = () => {
    const linkAddress = `${
      routes.termsAndConditions.url
    }${HOME_INSURANCE_TYPE}`;

    const quoteData = {
      ...state,
      consent: {
        emailConsent: values.email_consent,
        phoneConsent: values.phone_consent,
        postConsent: values.post_consent,
        mobileSmsConsent: values.mobile_sms_consent
      },
      analyzeTransaction: values.analyzeTransaction
    };
    saveInSessionStorage(
      FULL_DIRECT_DEBIT_STORAGE_IDENTIFIER,
      JSON.stringify(quoteData)
    );
    saveInSessionStorage(
      HOME_QUOTE_THANK_YOU_STORAGE,
      JSON.stringify(quoteData)
    );
    const sessionStorageLatestQuote = getObjectFromSessionStorage(
      `${HOME_LATEST_QUOTE_STORAGE}`
    );
    if (Object.keys(sessionStorageLatestQuote).length !== 0) {
      saveDataInLatestQuote(
        sessionStorageLatestQuote,
        'yourCover',
        quoteData.yourCoverObj
      );
    }
    if (
      isAgent() &&
      getItemFromSessionStorage(AGENT_CLICKED_REFERENCE_QUOTES)
    ) {
      removeFromSessionStorage(AGENT_CLICKED_REFERENCE_QUOTES);
    }

    history.push({
      pathname: linkAddress,
      state: quoteData
    });
  };

  const onDesktopClick = () => {
    if (insuranceType === HOME_INSURANCE_TYPE && userProfile?.quote) {
      saveInSessionStorage(HOME_RESUME_QUOTE, true);
      history.push('/wallet/getquote/aboutYou');
    }
  };

  useEffect(() => {
    setNextButton(JSON.parse(getItemFromSessionStorage(HOME_RESUME_QUOTE)));
  }, [getItemFromSessionStorage(AGENT_CLICKED_REFERENCE_QUOTES)]);

  const REQUIRED_KEYS = ['email_consent', 'phone_consent', 'post_consent', 'mobile_sms_consent'];   // <-- put your question keys here

  const isComplete = REQUIRED_KEYS.every((k: string) => touched[k]);

  const renderSubmitButton = () => (
    <div className={`${className}__button`}>
      <a
        data-ga
        id={`MarketingContent__getQuoteButton`}
        data-testid={`MarketingContent__getQuoteButton`}
      >
        <Button
          quaternary
          fluid
          onClick={() => {
            goToNextPage();
          }}
          disabled={!isComplete}
        >
          {!nextButton ? 'Get your quote' : 'Next'}
        </Button>
      </a>
    </div>
  );

  return (
    <div className={`${className}__container`} >
    <Form>
      <div
        className={`${commonFormStylesIdentifier}__nonDynamicFieldsContainer`}
      >
      <div className={`${className}`}>
        <div
          className={`${commonPageStylesIdentifier} ${desktopBackButtonClass(
            insuranceType
          )}`}
        >
          <MobileBackButton history={history} onDesktopClick={onDesktopClick} />
        </div>
        <ProgressBar
          stage={JOURNEY_YOUR_QUOTE}
          stages={
            insuranceType === HOME_INSURANCE_TYPE
              ? STAGES_HOME_JOURNEY
              : STAGES_MOTOR_TRAVEL_JOURNEY
          }
          labelText={
            insuranceType === HOME_INSURANCE_TYPE
              ? HOME_JOURNEY
              : MOTOR_TRAVEL_JOURNEY
          }
        />

        <CardWrapper alignItemsCenter={false}>
          <div className={`${commonPageStylesIdentifier}__pageTitle`} >
            <TitleWithUnderLine style={{ textAlign: 'center' }}>Your Marketing Preferences</TitleWithUnderLine>
          </div>
          <ConsentText />
          <div className={`${commonFormStylesIdentifier}__fieldContainer`}>
            <label htmlFor="verificationCode"
                   className={`${commonFormStylesIdentifier}__fieldLabel`}>
              <AccordionText
                  label="Can we send you marketing emails?"
                  icon="info"
                  iconAlign="right"
                  customLabelClass={`${commonFormStylesIdentifier}__accordionTextFieldLabel`}
                >
                  <div className={`${className}__infoText`}>
                    We will only send you marketing emails that we think you might be interested in.
                  </div>
                </AccordionText>
            </label>
            <div className={`${commonFormStylesIdentifier}--fieldLabelSpacing`}>
              <ButtonGroup
                name="email_consent"
                onClick={(value: boolean) => {
                  setFieldValue('email_consent', value === 'true');
                  setFieldTouched('email_consent', value);
                  saveData('marketingConsentObj','email_consent', value === 'true')
                }}
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false }
                ]}
                touched={touched.email_consent}
                selected={values.email_consent}
                error={isFieldError('email_consent', touched, errors)}
                errorMessage={errors.email_consent}
              />
            </div>
          </div>
          <div className={`${commonFormStylesIdentifier}__fieldContainer`}>
            <label htmlFor="verificationCode"
                   className={`${commonFormStylesIdentifier}__fieldLabel`}>
              <AccordionText
                label="Can we send you marketing push notifications on your Bank of Ireland mobile app?"
                icon="info"
                iconAlign="right"
                customLabelClass={`${commonFormStylesIdentifier}__accordionTextFieldLabel`}
              >
                <div className={`${className}__infoText`}>
                  We will only send you marketing push notifications on your Bank of Ireland mobile app that we think you might be interested in.
                </div>
              </AccordionText>
            </label>
            <div className={`${commonFormStylesIdentifier}--fieldLabelSpacing`}>
              <ButtonGroup
                key="mobile_sms_consent"
                name="mobile_sms_consent"
                onClick={(value: boolean) => {
                  setFieldValue('mobile_sms_consent', value === 'true');
                  setFieldTouched('mobile_sms_consent', value);
                  saveData('marketingConsentObj','mobile_sms_consent', value === 'true')
                }}
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false }
                ]}
                error={isFieldError('mobile_sms_consent', touched, errors)}
                errorMessage={errors?.mobile_sms_consent}
                selected={values?.mobile_sms_consent}
              />
            </div>
          </div>
          <div className={`${commonFormStylesIdentifier}__fieldContainer`}>
            <label htmlFor="verificationCode"
                   className={`${commonFormStylesIdentifier}__fieldLabel`}>
              <AccordionText
                label="Can we phone you with news of products, services and offers?"
                icon="info"
                iconAlign="right"
                customLabelClass={`${commonFormStylesIdentifier}__accordionTextFieldLabel`}
              >
                <div className={`${className}__infoText`}>
                  We will only phone you with news of products, services and offers that we think you might be interested in.
                </div>
              </AccordionText>
            </label>
            <div className={`${commonFormStylesIdentifier}--fieldLabelSpacing`}>
              <ButtonGroup
                key="phone_consent"
                name="phone_consent"
                onClick={(value: boolean) => {
                  setFieldValue('phone_consent', value === 'true');
                  setFieldTouched('phone_consent', value);
                  saveData('marketingConsentObj','phone_consent', value === 'true')
                }}
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false }
                ]}
                error={isFieldError('phone_consent', touched, errors)}
                errorMessage={errors?.phone_consent}
                selected={values?.phone_consent}
              />
            </div>
          </div>

          <div className={`${commonFormStylesIdentifier}__fieldContainer`}>
            <label htmlFor="verificationCode"
                   className={`${commonFormStylesIdentifier}__fieldLabel`}>
              Can we analyse your transactions to help identify products,
              services and offers we think you might be interested in?{' '}
            </label>
          </div>
          <div className={`${commonFormStylesIdentifier}--fieldLabelSpacing`}>
            <ButtonGroup
              key="post_consent"
              name="post_consent"
              onClick={(value: boolean) => {
                setFieldValue('post_consent', value === 'true');
                setFieldTouched('post_consent', value);
                saveData('marketingConsentObj','post_consent', value === 'true')
              }}
              options={[
                { label: 'Yes', value: true },
                { label: 'No', value: false }
              ]}
              error={isFieldError('post_consent', touched, errors)}
              errorMessage={errors?.post_consent}
              selected={values?.post_consent}
            />
          </div>
          <div
            className={`${commonFormStylesIdentifier}__infoText ${className}__consentInfoText`}
          >
            Understanding your transaction data from accounts you hold with Bank of Ireland, like a current or saving account, helps us to personalise product and offer information you receive from us.
          </div>
          <AnalyzeTransaction />
          <div className={`${className}__blueInfo`}>
            If you are an existing customer, any previous marketing preferences will be updated.
            This may take up to three workings days.{' '}
            <ExternalLink url={dataPrivacyHomeUrl} text="Learn about your marketing preferences and how we manage your personal data." />{' '}
          </div>
        </CardWrapper>

        <div className={`${className}__buttonContainer`}>
          {renderSubmitButton()}
          <div className={`${className}__backButton`}>
            <Link to={`${routes.getQuotePage.url}yourCover`}>
              <Button fluid={true} ariaLabelText={"Back button"}>Back</Button>
            </Link>
          </div>
        </div>
      </div>
      </div>
    </Form>
    </div>
  );
};

const marketingConsentYupSchema = yup.object().shape({});

const MarketingConsentPage = withFormik({
  mapPropsToValues(): {} {
    return {
      consent: false,
      email_consent: '',
      phone_consent: '',
      mobile_sms_consent: '',
      post_consent: '',
      analyzeTransaction: false,
      pushNotification: false,
      phoneOffer: false
    };
  },
  handleSubmit(values: DirectDebitType, { setSubmitting }: FormikProps) {
    setSubmitting(false);
  },
  validationSchema: marketingConsentYupSchema,
  displayName: 'MarketingConsentPage'
})(MarketingConsent);

export default MarketingConsentPage;
